import { ref } from 'vue'

/**
 * The one toast store for the whole app. ToastContainer.vue (mounted once in
 * App.vue) renders it; Sweep's ui store forwards its ui.toast() calls here.
 *
 * toast({ status, title, description?, actions?, duration? }) is the API.
 * show()/success()/error()/info()/warning() are the older message-only
 * helpers and stay for existing callers.
 */
export type ToastStatus = 'success' | 'error' | 'info' | 'warning'
export interface ToastAction { label: string; onClick?: () => void | Promise<void>; href?: string }
export interface ToastOptions { status: ToastStatus; title: string; description?: string; actions?: ToastAction[]; duration?: number }
export interface Toast {
    id: number
    /** The title. Named `message` for the older helpers and their tests. */
    message: string
    type: ToastStatus
    /** ms until auto-dismiss; 0 keeps it until dismissed */
    duration: number
    description?: string
    actions?: ToastAction[]
}

const MAX_VISIBLE = 3
const toasts = ref<Toast[]>([])
let nextId = 0
const timers = new Map<number, { handle: ReturnType<typeof setTimeout>; endsAt: number; remaining: number }>()

function arm(id: number, ms: number) {
    const handle = setTimeout(() => remove(id), ms)
    timers.set(id, { handle, endsAt: Date.now() + ms, remaining: ms })
}

function remove(id: number) {
    const t = timers.get(id)
    if (t) { clearTimeout(t.handle); timers.delete(id) }
    const index = toasts.value.findIndex(x => x.id === id)
    if (index !== -1) toasts.value.splice(index, 1)
}

/** Hover or focus holds the timer; resume() restarts it with what was left. */
function pause(id: number) {
    const t = timers.get(id)
    if (!t) return
    clearTimeout(t.handle)
    t.remaining = Math.max(0, t.endsAt - Date.now())
}
function resume(id: number) {
    const t = timers.get(id)
    if (t) arm(id, t.remaining)
}

function push(t: Omit<Toast, 'id'>) {
    const id = nextId++
    toasts.value.push({ id, ...t })
    while (toasts.value.length > MAX_VISIBLE) remove(toasts.value[0]!.id)
    if (t.duration > 0) arm(id, t.duration)
    return id
}

/** 5 s plain, 8 s with actions; an error with an action stays until dismissed. */
function defaultDuration(status: ToastStatus, actions?: ToastAction[]) {
    if (!actions?.length) return 5000
    return status === 'error' ? 0 : 8000
}

export function useToast() {
    const toast = (o: ToastOptions) => push({
        message: o.title, type: o.status, description: o.description,
        actions: o.actions?.slice(0, 2), duration: o.duration ?? defaultDuration(o.status, o.actions),
    })

    const show = (message: string, type: Toast['type'] = 'info', duration = 3000) => push({ message, type, duration })
    const success = (message: string, duration = 3000) => show(message, 'success', duration)
    const error = (message: string, duration = 5000) => show(message, 'error', duration)
    const info = (message: string, duration = 3000) => show(message, 'info', duration)
    const warning = (message: string, duration = 4000) => show(message, 'warning', duration)

    const clear = () => { toasts.value.slice().forEach(t => remove(t.id)) }

    return { toasts, toast, show, success, error, info, warning, remove, pause, resume, clear }
}
